/**
 * The registration token's one and only appearance, as a value.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE PLAINTEXT EXISTS IN EXACTLY ONE VARIANT OF THIS UNION, AND THERE IS NO
 *  FUNCTION BACK INTO IT.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Task 16 stores only a sha256 of the token and proved the plaintext is nowhere in
 * the database by scanning every row of `information_schema.columns`. `openapi.yaml`
 * says the same thing from the wire side: *"The plaintext `token` is returned exactly
 * once, at creation … there is no second read: an administrator who loses the token
 * mints another."* The browser is the last place that guarantee can be undone, and
 * the way it usually is undone is by accident — a value stashed "so the panel
 * survives a re-render", a list re-read that happens to carry the field, a copy
 * button that silently fails so the operator goes back for it.
 *
 * So the guarantee is structural here rather than procedural:
 *
 *  1. `token` is a member of the `shown` variant and of **no other variant**. A
 *     component handed a `spent` or `none` reveal cannot render the secret, because
 *     there is no property to read — a type error, not a discipline.
 *  2. `burn` is **total and irreversible**: `shown → spent`, `spent → spent`,
 *     `none → none`. There is no `unburn`, no `restore`, and `spent` carries no
 *     field the plaintext could hide in.
 *  3. Nothing here writes to storage of any kind. The value lives in React state and
 *     dies with the component; `web/src/screens/devices-token-once.test.tsx` guards
 *     the whole screen directory against `localStorage`, `sessionStorage`,
 *     `document.cookie` and `window.name` as a set comparison over the files that
 *     exist, so tomorrow's file is covered the moment somebody creates it.
 *  4. There is **no re-fetch that could produce it**. `RegistrationTokenSummary` —
 *     what `GET /devices/registration-tokens` returns — does not declare a `token`
 *     member, so reading one off a list row is a type error too.
 *
 * ── Why a union and not `token: string | null` ──────────────────────────────
 *
 * Because `null` admits two readings — "not minted yet" and "minted and gone" — and
 * they are different sentences on screen: the first offers a mint button, the second
 * says the token has been shown and will not be shown again, which is the only
 * warning that can still reach an operator who did not copy it. Collapsing them is
 * the same class of mistake as rendering an outage as a zero, one level down.
 */

/** What the `201` from `POST /devices/registration-tokens` carries that matters here. */
export interface MintedToken {
  readonly tokenId: string;
  readonly tokenPrefix: string;
  /** The plaintext. This is the only place in the client it is ever named. */
  readonly token: string;
  /**
   * `PULSE_PUBLIC_BASE_URL`-derived enrolment address. It deliberately does **not**
   * contain the token — a URL carrying a bearer credential ends up in browser
   * history, in a `Referer` and in a proxy access log — so the installer takes the
   * two as separate fields and this screen shows them as two separate fields.
   */
  readonly enrollmentUrl: string | null;
  readonly expiresAt: string;
  readonly maxUses: number;
}

/**
 * Never minted in this mounting of the screen. Offers the mint control.
 *
 * Reached again by nothing: `burn` maps `none` to `none`, so this is a start state
 * and not somewhere a spent reveal can be laundered back to.
 */
export interface RevealNone {
  readonly phase: 'none';
}

/** On screen, right now, for the only time. */
export interface RevealShown extends MintedToken {
  readonly phase: 'shown';
}

/**
 * Shown and gone. Carries the *prefix* — which `openapi.yaml` says is drawn from its
 * own random source at mint time and "carries no part of the secret" — so the panel
 * can still say **which** token it was, and nothing else.
 */
export interface RevealSpent {
  readonly phase: 'spent';
  readonly tokenId: string;
  readonly tokenPrefix: string;
}

export type Reveal = RevealNone | RevealShown | RevealSpent;

export const NO_REVEAL: Reveal = { phase: 'none' };

/** The `201` becomes the one variant that holds a secret. */
export function reveal(minted: MintedToken): Reveal {
  return { phase: 'shown', ...minted };
}

/**
 * The only transition out of `shown`, and it drops the token on the way.
 *
 * Total: every phase has an answer, and none of them is `shown`. Called when the
 * operator dismisses the panel, when a second token is minted, and — because React
 * state dies with the component — implicitly on every unmount and navigation.
 */
export function burn(state: Reveal): Reveal {
  if (state.phase === 'shown') {
    return { phase: 'spent', tokenId: state.tokenId, tokenPrefix: state.tokenPrefix };
  }
  return state;
}

/**
 * Whether a plaintext is on screen right now.
 *
 * A type predicate rather than a boolean, so the narrow it performs is the same one
 * the compiler uses: a caller that checks this gets `RevealShown` and a caller that
 * does not cannot reach `.token` at all.
 */
export function isShown(state: Reveal): state is RevealShown {
  return state.phase === 'shown';
}

/* ─────────────────────────────────────────────────────── the copy attempt ── */

/**
 * What happened when the operator pressed "copy" — and there is no silent branch.
 *
 * `navigator.clipboard` is `undefined` in an insecure context (plain HTTP on
 * anything but localhost, which is exactly how an on-premise deployment gets
 * installed first), and `writeText` **rejects** when the permission is refused or
 * the document is not focused. Both are ordinary, both are invisible, and both are
 * the worst possible failure on this screen: the operator believes they have the
 * token, navigates away, and the token no longer exists anywhere.
 *
 * So the outcome is a value the panel must render, `unavailable` is kept apart from
 * `failed` — "your browser will not let this page use the clipboard at all" and
 * "the copy was refused just now" are different things to do about it — and there is
 * no `undefined` meaning "nothing happened".
 */
export type CopyOutcome = 'idle' | 'copied' | 'failed' | 'unavailable';

/**
 * The clipboard call, with its two failure modes separated and neither swallowed.
 *
 * Takes the clipboard rather than reaching for `navigator` so the absent case is
 * reachable in a test without deleting a global: jsdom ships no clipboard, a browser
 * on http:// ships none either, and a test that could only exercise the happy path
 * would be certifying the branch that does not matter.
 */
export async function copyToClipboard(
  value: string,
  clipboard: Pick<Clipboard, 'writeText'> | undefined,
): Promise<CopyOutcome> {
  if (clipboard === undefined) return 'unavailable';
  try {
    await clipboard.writeText(value);
    return 'copied';
  } catch {
    return 'failed';
  }
}

/**
 * `navigator.clipboard`, if this document actually has one.
 *
 * TypeScript's DOM lib declares `Navigator.clipboard` as **non-optional**, which is a
 * lie in exactly the case that matters: it is `undefined` in an insecure context and
 * absent in jsdom. So the property is read as `unknown` and narrowed, rather than
 * trusted and then guarded — a guard on a type the compiler believes cannot be
 * falsy is a guard `@typescript-eslint/no-unnecessary-condition` deletes, taking the
 * real branch with it.
 */
export function browserClipboard(): Pick<Clipboard, 'writeText'> | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const candidate: unknown = (navigator as { clipboard?: unknown }).clipboard;
  if (candidate === null || typeof candidate !== 'object') return undefined;
  const writeText: unknown = (candidate as { writeText?: unknown }).writeText;
  return typeof writeText === 'function' ? (candidate as Pick<Clipboard, 'writeText'>) : undefined;
}
